import { Download, Loader2 } from 'lucide-react'

import { resolveReportPeriodBounds } from '@/lib/reports/filters'
import { SALON_REPORT_PERIOD_PRESETS } from '@/lib/reports/salonReportFilters'
import { cn } from '@/lib/utils'

/**
 * Simple date filters + CSV export for Salon Desk sales report.
 *
 * @param {object} props
 * @param {string} props.period
 * @param {string} props.customFrom
 * @param {string} props.customTo
 * @param {(period: string) => void} props.onPeriodChange
 * @param {(value: string) => void} props.onCustomFromChange
 * @param {(value: string) => void} props.onCustomToChange
 * @param {() => void} [props.onExportCsv]
 * @param {boolean} [props.exporting]
 * @param {boolean} [props.loading]
 */
export function SalonReportFilterBar({
  period,
  customFrom,
  customTo,
  onPeriodChange,
  onCustomFromChange,
  onCustomToChange,
  onExportCsv,
  exporting = false,
  loading = false,
}) {
  let bounds = null
  try {
    bounds = resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
  } catch {
    bounds = null
  }

  return (
    <div className="salon-panel space-y-3 p-4">
      <div className="flex flex-wrap gap-2">
        {SALON_REPORT_PERIOD_PRESETS.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onPeriodChange(option.id)}
            className={cn(
              'rounded-xl px-4 py-2 text-sm font-semibold transition-colors',
              period === option.id
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-muted/70 text-foreground hover:bg-muted',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {period === 'custom' ? (
          <>
            <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
              From
              <input
                type="date"
                value={customFrom}
                onChange={(e) => onCustomFromChange(e.target.value)}
                className="h-10 rounded-xl border border-border/80 bg-background px-3 text-sm text-foreground"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
              To
              <input
                type="date"
                value={customTo}
                onChange={(e) => onCustomToChange(e.target.value)}
                className="h-10 rounded-xl border border-border/80 bg-background px-3 text-sm text-foreground"
              />
            </label>
          </>
        ) : bounds ? (
          <p className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">{bounds.from}</span>
            {' to '}
            <span className="font-medium text-foreground">{bounds.to}</span>
          </p>
        ) : null}

        {onExportCsv ? (
          <button
            type="button"
            disabled={loading || exporting}
            onClick={onExportCsv}
            className="ml-auto inline-flex h-10 items-center gap-2 rounded-xl border border-border/80 bg-background px-4 text-sm font-semibold hover:bg-muted/60 disabled:opacity-50"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Download className="h-4 w-4" aria-hidden />
            )}
            Export CSV
          </button>
        ) : null}
      </div>
    </div>
  )
}
